import React, { useState, useEffect, useCallback } from 'react';
import { HelpCircle } from 'lucide-react';
import ScreenTour from './ScreenTour';
import { getTour } from '@/lib/screenTours';

/**
 * TourLauncher — a small "?" help button that opens the guided tour for the
 * current screen/tab, and auto-shows it once on first visit.
 *
 * Props:
 *  - tourKey: key into SCREEN_TOURS (e.g. 'hub-contractors', 'project-loan')
 *  - userId: used to scope the "seen" flag per user (optional)
 *  - variant: 'icon' (default, ghost round button) or 'inline'
 *  - className: extra classes for the trigger
 */
export const TourLauncher = ({ tourKey, userId = 'anon', variant = 'icon', className = '' }) => {
  const [open, setOpen] = useState(false);
  const tour = getTour(tourKey);
  const storageKey = tourKey ? `tour_seen_${userId}_${tourKey}` : null;
  // Option B: auto-pop a tour only ONCE per user across the whole app. After the
  // first auto-shown tour is set, no other tour auto-opens — they stay available
  // on demand via the "?" launcher. Prevents tour overlays from blocking clicks
  // (e.g. tapping a project) during normal use and live demos.
  const autoShownKey = `tour_autoshown_${userId}`;

  // Auto-show once, globally, on the user's first encounter.
  useEffect(() => {
    if (!tour || !storageKey) return;
    let globallyShown = false;
    let seenThis = false;
    try {
      globallyShown = localStorage.getItem(autoShownKey) === '1';
      seenThis = localStorage.getItem(storageKey) === '1';
    } catch (e) { globallyShown = true; }
    if (!globallyShown && !seenThis) {
      // Claim the global auto-show slot immediately so sibling/subsequent
      // TourLaunchers (other tabs/screens) don't also auto-pop.
      try { localStorage.setItem(autoShownKey, '1'); } catch (e) { /* ignore */ }
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [tour, storageKey, autoShownKey]);

  const handleClose = useCallback(() => {
    setOpen(false);
    try { if (storageKey) localStorage.setItem(storageKey, '1'); } catch (e) { /* ignore */ }
  }, [storageKey]);

  if (!tour) return null;

  return (
    <>
      {variant === 'inline' ? (
        <button
          onClick={() => setOpen(true)}
          className={`inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 ${className}`}
          data-testid={`tour-launch-${tourKey}`}
        >
          <HelpCircle className="w-4 h-4" /> How this works
        </button>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors flex-shrink-0 ${className}`}
          title="How this screen works"
          aria-label="How this screen works"
          data-testid={`tour-launch-${tourKey}`}
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      )}
      <ScreenTour open={open} onClose={handleClose} tour={tour} />
    </>
  );
};

export default TourLauncher;
